/** The page's own furniture: a stat, a labelled field, a spec row, a JSON blob. */

import { Card, CardContent } from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import { cn } from "~/lib/utils";
import { HighlightedCode } from "~/pages/manifold-nextgen/components/conversations/HighlightedCode";
import { USER_MESSAGE_BUBBLE_CLS } from "~/pages/manifold-nextgen/components/conversations/UserMessage";

/**
 * One headline number, as a tile. Shared by the dashboard's KPI row and the run
 * page's summary, which are the same kind of statement.
 *
 * Values use proportional figures: at this size `tabular-nums` gives every digit
 * a `0`'s width and makes a number like 121 look loose.
 */
export const Stat = ({
  label,
  value,
  unit,
  detail,
  children,
}: {
  label: string;
  /** A node so a tile can stand a placeholder where its figure will go. */
  value: React.ReactNode;
  /** Rendered smaller than `value`, for a unit or a denominator. */
  unit?: string;
  detail?: React.ReactNode;
  /** Sits on the value's baseline, for a delta or a badge. */
  children?: React.ReactNode;
}) => (
  <Card className="gap-0 py-4 shadow-none">
    <CardContent className="px-4">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
        <span className="text-3xl font-semibold text-foreground">
          {value}
          {unit && <span className="text-lg font-normal text-muted-foreground">{unit}</span>}
        </span>
        {children}
      </div>
      {detail && <p className="mt-1 text-xs text-muted-foreground">{detail}</p>}
    </CardContent>
  </Card>
);

/** One labelled form control, with an optional hint under it. */
export const Field = ({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={htmlFor}>{label}</Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

/** A labelled fact in a run's summary grid, so the two grids line up. */
export const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-1">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <div className="min-w-0">{children}</div>
  </div>
);

/**
 * The question a case sends the agent, wearing the conversation view's own sent
 * bubble: this is not a field about the case, it is the user turn the run
 * replays, and it sits among JSON blobs that a bordered callout looks like.
 */
export const CaseQuery = ({ query }: { query: string }) => (
  <div className="flex justify-end">
    <div className={cn(USER_MESSAGE_BUBBLE_CLS, "max-w-[90%]")}>
      <p className="whitespace-pre-wrap">{query}</p>
    </div>
  </div>
);

/**
 * A titled block of panel content. Solid ink in sentence case rather than a muted
 * uppercase eyebrow, which on a panel of six facts leaves the labels louder than
 * what they label.
 */
export const Section = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <section>
    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
    <div className="mt-2">{children}</div>
  </section>
);

/**
 * A run of short label/value facts as one bordered strip. The label track is
 * shared by every row and sized to the longest label, so the values line up and
 * a long label gets its line without wrapping.
 */
export const Spec = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <dl
    className={cn(
      "grid grid-cols-[minmax(6rem,max-content)_1fr] divide-y divide-border overflow-hidden rounded-md border border-border",
      className
    )}
  >
    {children}
  </dl>
);

/** One row of a `Spec`, borrowing the strip's two columns. */
export const SpecRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="col-span-2 grid grid-cols-subgrid items-baseline gap-x-4 px-3 py-2">
    <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
    <dd className="min-w-0 text-sm text-foreground">{children}</dd>
  </div>
);

/** The em dash a `Spec` row uses where the field is genuinely unset. */
export const SpecEmpty = ({ children }: { children: React.ReactNode }) => (
  <span className="text-sm text-muted-foreground">{children}</span>
);

// The same treatment a tool call gives its `code` field. The highlighter emits
// one div per line, which is what lets each line hang-indent its own wrap —
// without it a long `expected_answer` continues at column zero and reads as a key.
const JSON_BLOCK_CLS =
  "overflow-x-auto whitespace-pre-wrap break-words rounded-md border border-border/60 bg-muted/40 px-3 py-2 pr-10 font-mono text-[11px] leading-relaxed [&>div]:-indent-4 [&>div]:pl-4";

/**
 * A JSONB blob, syntax-highlighted and copyable, sharing the conversation view's
 * Prism highlighter. Expectation keys and their values are the whole content, and
 * structural colour is what separates the two at 11px.
 */
export const JsonBlock = ({ value }: { value: unknown }) => (
  <HighlightedCode
    code={JSON.stringify(value, null, 2)}
    language="json"
    className={JSON_BLOCK_CLS}
    copyOnHoverOnly
  />
);
